import {Http} from "@angular/http";
import {Observable} from "rxjs/Observable";
import 'rxjs/add/observable/timer';
import {EventEmitter, Injectable} from '@angular/core';
import {FileModel} from "../models/file.model";
import {environment} from "../../../environments/environment";

/**
 * Synchronous chunked multi-file upload
 */
@Injectable()
export class UploaderService {

  // input element
  private fileEl: HTMLInputElement;

  // url to CDN server
  private serverUrl: string;

  private xhr: XMLHttpRequest;

  // list of all added files
  files: Array<FileModel> = [];

  // stores upload speed of the current uploaded file
  uploadSpeed: number = 0;

  // events
  onAdd: EventEmitter<any> = new EventEmitter();
  onDone: EventEmitter<any> = new EventEmitter();

  constructor(private http: Http) {
    // set cdn url
    this.serverUrl = environment.cdnUrl;
  }

  reset() {
    this.files = [];
    this.uploadSpeed = 0;
    this.onAdd = new EventEmitter();
    this.onDone = new EventEmitter();
  }

  /**
   * Opens native file dialog
   */
  browse(type: string) {
    // create new file input
    this.fileEl = document.createElement('input');
    this.fileEl.type = 'file';

    // show only accepted file types
    if (type === FileModel.TYPE_IMAGE) {
      this.fileEl.accept = '.jpg,.jpeg,.png,.gif';
    } else if (type === FileModel.TYPE_VIDEO) {
      this.fileEl.accept = '.mp4,.mpeg,.avi,.webm';
    }

    // open file window
    this.fileEl.click();

    // on file choose event
    let handler = e => {
      this.add(this.fileEl.files.item(0), type);

      e.target.removeEventListener(e.type, handler);
    }
    this.fileEl.addEventListener('change', handler);
  }

  /**
   * Add file to queue
   */
  add(file: File, type: string) {
    this.onAdd.emit(file);

    this.files.push(new FileModel(file, type));

    // try to start upload
    this.upload();
  }

  /**
   * Cancel file upload
   */
  cancel(file: FileModel) {
    if (file.status === FileModel.STATUS_PROGRESS) {
      this.xhr.abort();
    }

    this.files.splice(this.files.indexOf(file), 1);
  }

  /**
   * Reset model and try upload again (can be used after upload fail)
   */
  retry(file: FileModel) {
    file.progress = 0;
    file.loadedChunks = 0;
    file.status = FileModel.STATUS_QUEUE;

    this.upload();
  }

  /**
   * Start synchronously upload files from queue
   */
  upload() {
    // prevent async upload
    if (this.files.find(x => x.status === FileModel.STATUS_PROGRESS)) {
      return;
    }

    // retrieve first not uploaded file
    let file = this.files.find(x => x.status === FileModel.STATUS_QUEUE);

    if (!file) {
      // empty queue
      return;
    }

    // upload chunk
    this.processChunk(file);
  }

  /**
   * Process single chunk
   */
  private processChunk(file: FileModel) {
    let xhr = new XMLHttpRequest();
    let formData = new FormData();
    let startTime = 0;

    this.xhr = xhr;

    // set necessary post data
    formData.append('type', file.type);
    formData.append('flowIdentifier', file.name);
    formData.append('flowTotalSize', String(file.file.size));
    formData.append('flowChunkNumber', String(file.loadedChunks + 1));

    let totalChunks = Math.ceil(file.file.size / FileModel.CHUNK_SIZE);
    formData.append('flowTotalChunks', String(totalChunks));

    let blob = file.file.slice(file.loadedChunks * FileModel.CHUNK_SIZE, (file.loadedChunks + 1) * FileModel.CHUNK_SIZE, file.file.type);
    formData.append('file', blob);

    // handle chunk upload complete callback
    xhr.addEventListener('load', e => {
      file.loadedChunks++;

      // update upload speed
      this.uploadSpeed = FileModel.CHUNK_SIZE / (new Date().getTime() - startTime);

      // update progress
      file.progress = Math.round((file.loadedChunks / totalChunks) * 100);

      // detect last chunk
      let bytesLoaded = file.loadedChunks * FileModel.CHUNK_SIZE;
      if (bytesLoaded >= file.file.size) {
        if (file.type === FileModel.TYPE_VIDEO) {
          this.processUploadedFile(file, xhr.response);
        } else {
          // processing status
          file.status = FileModel.STATUS_UPLOADED;
          file.progress = 100;

          this.onDone.emit({file: file, id: xhr.response});

          // upload next file in queue
          this.upload();
        }
        return;
      }

      // process next chunk
      this.processChunk(file);
    });

    // handle error
    xhr.addEventListener('error', e => {
      file.status = FileModel.STATUS_FAILED;
    });

    // handle abort
    xhr.addEventListener('abort', e => {
      // remove file
      this.files.splice(this.files.indexOf(file), 1);
    });

    // upload start
    file.status = FileModel.STATUS_PROGRESS;
    xhr.open('POST', this.serverUrl + 'upload');
    xhr.send(formData);

    // remember time of request start (its required for calculate upload speed)
    startTime = new Date().getTime();
  }

  /**
   * Handle file processing
   */
  private processUploadedFile(file: FileModel, jobId: number) {
    // processing status
    file.status = FileModel.STATUS_PROCESSING;
    file.progress = 0;

    // check processing progress periodically
    let timer = Observable.timer(0, 1000).subscribe(() => {
      this.http.get(this.serverUrl + 'api/job/' + jobId).subscribe(res => {
        let job = res.json();

        file.progress = job.progress;

        // check is that job completed
        if (job.state === 'complete') {
          // stop timer
          timer.unsubscribe();

          // set file as completed
          file.status = FileModel.STATUS_UPLOADED;

          file.name = job.data.file;
          console.debug(job);
          this.onDone.emit({file: file, id: job.data.fileId});

          // upload next file in queue
          this.upload();
        }
      });
    });
  }

}



// WEBPACK FOOTER //
// /code/frontend/src/app/shared/services/uploader.service.ts